import { Injectable, computed, signal } from '@angular/core';
import {
  ChartTypeOption,
  CustomAggregation,
  DashboardDraftItem,
  DashboardStatus,
  DashboardTab,
  DatasetColumn,
  GlobalFilter,
  GlobalFiltersPosition,
  SavedSelfServiceChart,
  SavedSelfServiceDashboard,
  UploadedDataset,
} from './self-service-types';

/**
 * Cross-stage shared state for the self-service feature, backed by Angular
 * signals (Angular 20 — the project's default reactivity primitive; it had no
 * prior signal/RxJS-state convention to match).
 *
 * This is the keystone for splitting the monolithic
 * SelfServiceVisualizationComponent into data-builder / chart-builder /
 * dashboard-builder sub-components: each stage reads and writes the SAME signals
 * here instead of threading 40+ @Input/@Output bindings between siblings.
 *
 * Only state that genuinely crosses stage boundaries lives here:
 *   - dataset        : produced by data-builder, consumed by chart-builder
 *   - chart working  : owned by chart-builder, read by dashboard-builder on save
 *   - savedCharts    : produced by chart-builder, consumed by dashboard-builder
 *   - dashboard      : owned by dashboard-builder
 *
 * Purely-local UI state (in-progress edit fields, transient messages, picker
 * visibility) stays inside each component and must NOT be hoisted here.
 *
 * Fields are migrated into this service incrementally, one slice per component
 * carve-out, so each move is small and independently verifiable.
 */
@Injectable({ providedIn: 'root' })
export class SelfServiceStateService {
  // ── Dataset (data-builder → chart-builder) ──────────────────────────────
  /** Raw uploaded/entity dataset before computed columns + row filters. */
  readonly rawDataset = signal<UploadedDataset | undefined>(undefined);
  /** Active dataset with computed columns / row filter applied. */
  readonly dataset = signal<UploadedDataset | undefined>(undefined);
  /** User-defined custom aggregations available to chart aggregation. */
  readonly customAggregations = signal<CustomAggregation[]>([]);
  /** Available chart-type definitions (loaded once; read by chart-builder for the
   *  picker and by dashboard rendering for previewKind/label lookups). */
  readonly chartTypes = signal<ChartTypeOption[]>([]);

  // NOTE: the chart's LIVE working state (mapping, chartStyle, selectedChartType,
  // seriesField, shape options, filter metadata) is intentionally NOT here — it
  // stays local to ChartBuilderComponent. The dashboard never reads the builder's
  // live working copy; it reads the SAVED chart's own snapshot (see SavedSelfServiceChart),
  // so hoisting that state would add coupling for no consumer.

  // ── Saved charts (chart-builder → dashboard-builder) ─────────────────────
  readonly savedCharts = signal<SavedSelfServiceChart[]>([]);
  readonly activeSavedChartId = signal<string>('');

  // ── Dashboard (dashboard-builder) ────────────────────────────────────────
  readonly dashboardName = signal<string>('Untitled dashboard');
  readonly dashboardItems = signal<DashboardDraftItem[]>([]);
  readonly dashboardColumns = signal<number>(3);
  readonly dashboardStatus = signal<DashboardStatus>('draft');
  readonly dashboardPublishedAt = signal<string | null>(null);
  readonly dashboardGlobalFilters = signal<GlobalFilter[]>([]);
  readonly dashboardGlobalFiltersPosition = signal<GlobalFiltersPosition>('top');
  readonly activeGlobalFilters = signal<Record<string, string[]>>({});
  /** Tabs for a tabbed dashboard (empty = single-page dashboard). */
  readonly dashboardTabs = signal<DashboardTab[]>([]);
  /** The tab the builder is currently editing (which charts show + where newly
   *  added charts land). Null for single-page dashboards. */
  readonly activeBuilderTabId = signal<string | null>(null);
  readonly savedDashboards = signal<SavedSelfServiceDashboard[]>([]);
  readonly activeSavedDashboardId = signal<string>('');

  /** One-shot intent for which step the dashboard-builder should open on when it
   *  next mounts (e.g. set to 'setup' by the chart-builder's "Add to dashboard"
   *  flow so the user starts at Setup rather than landing on Arrange/Preview).
   *  The dashboard-builder consumes and clears it in ngAfterViewInit. */
  readonly pendingDashboardStep = signal<'setup' | null>(null);

  // ── Derived selectors (replace the monolith's hand-rolled getter caches) ─
  /** Numeric columns of the active dataset (for value/size mappings). */
  readonly numericColumns = computed<DatasetColumn[]>(() =>
    (this.dataset()?.columns ?? []).filter((column) => column.type === 'number'),
  );

  /** Category-eligible columns of the active dataset (anything non-empty). */
  readonly categoryColumns = computed<DatasetColumn[]>(() =>
    (this.dataset()?.columns ?? []).filter((column) => column.type !== 'empty'),
  );

  /** The currently active saved chart, if any. */
  readonly activeSavedChart = computed<SavedSelfServiceChart | undefined>(() =>
    this.savedCharts().find((chart) => chart.id === this.activeSavedChartId()),
  );

  /** True once a dataset has been loaded. */
  readonly hasDataset = computed<boolean>(() => !!this.dataset());

  // ── Mutators ─────────────────────────────────────────────────────────────
  // Thin wrappers so call sites read intent-fully and so the persistence layer
  // can later hook a single place per slice.

  setDataset(dataset: UploadedDataset | undefined): void {
    this.dataset.set(dataset);
  }

  setRawDataset(dataset: UploadedDataset | undefined): void {
    this.rawDataset.set(dataset);
  }

  setSavedCharts(charts: SavedSelfServiceChart[]): void {
    this.savedCharts.set(charts);
  }

  setSavedDashboards(dashboards: SavedSelfServiceDashboard[]): void {
    this.savedDashboards.set(dashboards);
  }

  setDashboardItems(items: DashboardDraftItem[]): void {
    this.dashboardItems.set(items);
  }

  setActiveSavedChartId(id: string): void {
    this.activeSavedChartId.set(id);
  }

  setCustomAggregations(aggregations: CustomAggregation[]): void {
    this.customAggregations.set(aggregations);
  }

  setChartTypes(chartTypes: ChartTypeOption[]): void {
    this.chartTypes.set(chartTypes);
  }

  setDashboardName(name: string): void {
    this.dashboardName.set(name);
  }

  setDashboardColumns(columns: number): void {
    this.dashboardColumns.set(columns);
  }

  setDashboardStatus(status: DashboardStatus): void {
    this.dashboardStatus.set(status);
  }

  setDashboardPublishedAt(value: string | null): void {
    this.dashboardPublishedAt.set(value);
  }

  setDashboardGlobalFilters(filters: GlobalFilter[]): void {
    this.dashboardGlobalFilters.set(filters);
  }

  setDashboardGlobalFiltersPosition(position: GlobalFiltersPosition): void {
    this.dashboardGlobalFiltersPosition.set(position);
  }

  setActiveGlobalFilters(filters: Record<string, string[]>): void {
    this.activeGlobalFilters.set(filters);
  }

  setDashboardTabs(tabs: DashboardTab[]): void {
    this.dashboardTabs.set(tabs);
  }

  setActiveBuilderTabId(id: string | null): void {
    this.activeBuilderTabId.set(id);
  }

  setActiveSavedDashboardId(id: string): void {
    this.activeSavedDashboardId.set(id);
  }

  setPendingDashboardStep(step: 'setup' | null): void {
    this.pendingDashboardStep.set(step);
  }
}
